// ============================================================
// TMR Portal Admin — Contratos (rota /contratos): o portfólio real
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { PageHeader, FilterTabs } from '../../components/ui';
import { Pagination } from '../../components/Pagination';
import { RecordPicker } from '../../components/RecordPicker';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons } from '../../lib/icons';
import { maskCNPJ } from '../../lib/format';
import { useCompany, useCompanySearch } from '../../data/companies/queries';
import type { CompanyListItem } from '../../data/companies/contract';
import { canOperate } from '../../data/companies/status';
import { CONTRACT_PAGE_SIZE, contractExportPage, useContractCounts, useContractList } from '../../data/contracts/queries';
import { ExportMenu } from '../../components/ExportMenu';
import { CONTRACT_EXPORT_COLUMNS } from './exportColumns';
import type { ContractRecord, ContractStatus } from '../../data/contracts/contract';
import { CONTRACT_STATUS_OPTIONS } from '../../data/contracts/status';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { ContractRow } from './ContractRow';
import { ContractDetailPanel } from './ContractDetailPanel';
import { NewContractModal } from './NewContractModal';
import type { ContractCompany } from './NewContractModal';

/** The status filter: every status, or one of them. */
type StatusFilter = 'all' | ContractStatus;

const TABS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  ...CONTRACT_STATUS_OPTIONS.map((o) => ({ value: o.value as StatusFilter, label: o.label })),
];

/**
 * Every contract the operator can see, newest first.
 *
 * There is no search box and no sorting: the list endpoint filters by party and status and orders by
 * creation, and filtering the ten rows already on screen in the browser would claim to have searched
 * everything (09, lacuna L2). The "Pendentes" tab is the closest thing to an alert for contracts that were
 * drawn up and never sent — which is where a failed send leaves them.
 */
export function ContractsPage() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [openContractId, setOpenContractId] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [newFor, setNewFor] = useState<ContractCompany | null>(null);

  const canManage = usePermission(PERMISSIONS.contractsManage);
  // The picker reads the Empresas directory, so it is only offered with that permission: a picker that 403s
  // on every keystroke is worse than not offering the search (see RecordPicker).
  const canPickCompany = usePermission(PERMISSIONS.companyRead);

  const counts = useContractCounts();

  // Described once: the table pages through this filter and the export walks all of it.
  const filters = { status: status === 'all' ? undefined : status };
  const list = useContractList({ ...filters, page });

  const stats = [
    { label: 'Contratos', value: counts.total },
    { label: 'Pendentes de envio', value: counts.pending },
    { label: 'Aguardando assinatura', value: counts.awaitingSignature },
    { label: 'Assinados', value: counts.signed },
  ];

  const items = list.data?.items ?? [];
  const totalCount = list.data?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / (list.data?.pageSize ?? CONTRACT_PAGE_SIZE)));

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Portfólio"
        title="Contratos"
        desc="Contratos de credenciamento das empresas, da emissão à assinatura."
        actions={
          <>
            <ExportMenu
              spec={{
                fileName: 'contratos',
                columns: CONTRACT_EXPORT_COLUMNS,
                fetchPage: contractExportPage(filters),
              }}
              totalCount={totalCount}
              disabled={list.isPending}
            />
            {canManage && canPickCompany && (
              <Btn variant="primary" icon={Icons.plus} onClick={() => setPicking(true)}>
                Novo contrato
              </Btn>
            )}
          </>
        }
      />

      <div className="pa-kpi-grid">
        {stats.map((s) => (
          <div key={s.label} className="pa-card" style={{ padding: 18 }}>
            <div className="pa-kpi-label" style={{ marginBottom: 8 }}>{s.label}</div>
            <div className="pa-num" style={{ fontSize: 26, lineHeight: 1 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <FilterTabs
        tabs={TABS}
        value={status}
        onChange={(v) => {
          setStatus(v as StatusFilter);
          setPage(1);
        }}
      />

      {list.isPending && <Skeleton rows={6} />}

      {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

      {list.data && items.length === 0 && (
        <div className="pa-card">
          <EmptyState
            message={
              status === 'all'
                ? 'Nenhum contrato emitido ainda.'
                : 'Nenhum contrato com esta situação.'
            }
            hint={
              status === 'all'
                ? 'Um contrato é criado a partir da empresa, e o envio para assinatura é o passo seguinte.'
                : 'Troque o filtro para ver os contratos em outras situações.'
            }
          />
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="pa-card" style={{ padding: 0, overflow: 'hidden' }}>
            {items.map((contract) => (
              <PortfolioRow
                key={contract.id}
                contract={contract}
                canManage={canManage}
                canReadCompany={canPickCompany}
                onOpen={() => setOpenContractId(contract.id)}
              />
            ))}
          </div>

          <Pagination page={page} pageCount={pageCount} total={totalCount} onChange={setPage} />
        </>
      )}

      <ContractDetailPanel
        contractId={openContractId}
        canManage={canManage}
        onClose={() => setOpenContractId(null)}
      />

      {picking && (
        <RecordPicker<CompanyListItem>
          eyebrow="Novo contrato"
          title="Para qual empresa?"
          subtitle="O contrato é emitido para a empresa escolhida, e quem assina é o representante legal do cadastro dela."
          placeholder="Buscar empresa, CNPJ, domínio…"
          useSearch={useCompanySearch}
          getKey={(company) => company.id}
          toRow={(company) => ({
            primary: company.name,
            secondary: `${company.cnpj ? maskCNPJ(company.cnpj) : 'Sem CNPJ'} · ${company.address.city}/${company.address.state}`,
          })}
          rowIcon={Icons.clinic}
          idleMessage="Busque por nome, CNPJ ou domínio."
          notFoundMessage="Nenhuma empresa encontrada."
          onSelect={(company) => {
            setPicking(false);
            // The list item has no representative — the modal needs one, so it is resolved from the record.
            setNewFor({
              id: company.id,
              name: company.name,
              cnpj: company.cnpj ?? '',
              legacyProducerId: company.legacyProducerId,
              representative: { name: null, email: null, phone: null },
              active: canOperate(company.status),
            });
          }}
          onClose={() => setPicking(false)}
        />
      )}

      {newFor && (
        <NewContractForCompany
          company={newFor}
          onClose={() => setNewFor(null)}
          onCreated={(contract) => setOpenContractId(contract.id)}
        />
      )}
    </div>
  );
}

/**
 * A contract row that names its Empresa.
 *
 * The list only carries `partyId` (09, lacuna L1), so the name is resolved per row. Each lookup is cached
 * under the Empresa's own key and shared with its detail screen, so opening one afterwards is already warm.
 * Without `company.read` the row shows the id and says so — having `contracts.read` without it is a legitimate
 * combination, and inventing a name would be worse than admitting the lookup was refused.
 */
function PortfolioRow({
  contract,
  canManage,
  canReadCompany,
  onOpen,
}: {
  contract: ContractRecord;
  canManage: boolean;
  canReadCompany: boolean;
  onOpen: () => void;
}) {
  const company = useCompany(canReadCompany ? contract.partyId : undefined);

  const lead = !canReadCompany ? (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{contract.partyId.slice(0, 8)}</span>
  ) : company.data ? (
    company.data.name
  ) : company.isError ? (
    <span style={{ color: 'var(--fg-subtle)' }}>Empresa fora do seu escopo</span>
  ) : (
    <span style={{ color: 'var(--fg-subtle)' }}>Carregando empresa…</span>
  );

  return <ContractRow contract={contract} canManage={canManage} onOpen={onOpen} lead={lead} />;
}

/**
 * Resolves the Empresa's record before opening the creation modal.
 *
 * The picker hands over a list item, which has no representative — and the modal has to show who will be
 * bound by the contract before it is drawn up, not after.
 */
function NewContractForCompany({
  company,
  onClose,
  onCreated,
}: {
  company: ContractCompany;
  onClose: () => void;
  onCreated: (contract: ContractRecord) => void;
}) {
  const record = useCompany(company.id);

  if (record.isPending) return null;

  if (record.isError) {
    return (
      <div className="pa-card" style={{ position: 'fixed', inset: 'auto 24px 24px auto', zIndex: 70, maxWidth: 420 }}>
        <ErrorState
          error={record.error}
          onRetry={record.error instanceof ApiError && record.error.isNotFound ? undefined : () => void record.refetch()}
        />
        <div style={{ padding: '0 18px 18px', textAlign: 'center' }}>
          <Btn variant="secondary" onClick={onClose}>Fechar</Btn>
        </div>
      </div>
    );
  }

  return (
    <NewContractModal
      company={{
        id: record.data.id,
        name: record.data.name,
        cnpj: record.data.cnpj ?? '',
        legacyProducerId: record.data.legacyProducerId,
        representative: {
          name: record.data.representative?.name ?? null,
          email: record.data.representative?.email ?? null,
          phone: record.data.representative?.phone ?? null,
        },
        active: canOperate(record.data.status),
      }}
      onClose={onClose}
      onCreated={onCreated}
    />
  );
}
